import React, { useState, useRef, useEffect } from 'react'
import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  markdownShortcutPlugin,
  toolbarPlugin,
  UndoRedo,
  BoldItalicUnderlineToggles,
  BlockTypeSelect,
  ListsToggle,
  InsertThematicBreak,
  CreateLink,
  Separator,
} from '@mdxeditor/editor'

// The secondary formatting controls. Rendered inline when the field is wide and,
// in a separate copy, inside the overflow menu when it is narrow. A container
// query in index.css shows exactly one copy at a time.
function SecondaryControls() {
  return (
    <>
      <BlockTypeSelect />
      <ListsToggle />
      <Separator />
      <InsertThematicBreak />
      <CreateLink />
    </>
  )
}

// A "⋮" button that reveals the secondary controls in a dropdown panel. Shown
// only on narrow widths (see the container query in index.css).
function OverflowMenu({ children }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  return (
    <div className="toolbar-overflow-menu" ref={ref}>
      <button
        type="button"
        className="toolbar-overflow-trigger"
        aria-label="More formatting options"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        &#8942;
      </button>
      {open && <div className="toolbar-overflow-panel">{children}</div>}
    </div>
  )
}

function ToolbarContents() {
  return (
    <>
      <UndoRedo />
      <Separator />
      <BoldItalicUnderlineToggles />
      <div className="toolbar-overflow-inline">
        <Separator />
        <SecondaryControls />
      </div>
      <OverflowMenu>
        <SecondaryControls />
      </OverflowMenu>
    </>
  )
}

const toolbar = toolbarPlugin({
  toolbarClassName: 'custom-toolbar',
  toolbarContents: () => <ToolbarContents />,
})

const contentPlugins = [
  headingsPlugin(),
  listsPlugin(),
  quotePlugin(),
  thematicBreakPlugin(),
  markdownShortcutPlugin(),
]

export default function App({ markdown = '', definition = {}, onChange }) {
  const readOnly = definition.enabled === false
  const showLabel = definition.label && !definition.hideLabel
  const plugins = readOnly ? contentPlugins : [toolbar, ...contentPlugins]

  return (
    <>
      {showLabel && (
        <label style={{ display: 'block', marginBottom: 4, color: definition.mandatory ? '#d32f2f' : 'inherit' }}>
          {definition.label}{definition.mandatory ? ' *' : ''}
        </label>
      )}
      <div style={{ borderRadius: 6, padding: 12, background: '#f0f0f3' }}>
        <MDXEditor markdown={markdown} readOnly={readOnly} onChange={onChange} plugins={plugins} />
      </div>
    </>
  )
}
